/**
 * Formulärfälten för kundprojekt – en uppsättning som används av både
 * registreringen (KundprojektDialog) och redigeringen (KundprojektRedigering)
 * på leadsidan, kundkortet och i kundprojektvyn.
 *
 * Alla fält är kontrollerade: kp + satt(falt, varde) / setKp(funktion).
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, ToggleButtonGroup, ToggleButton, Autocomplete, IconButton,
  Checkbox, Tooltip, CircularProgress, Divider,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { SWEDISH_MUNICIPALITIES } from '../../utils/swedishMunicipalities';
import {
  nyPart, visaSakerhetsfalt, visaSakerhetsvarning, SAKERHET_VARNING, SAKERHET_NAMN_HJALP,
  KP_TYPER, KP_BYGGSKEDEN, KP_ENTREPRENADFORMER, KP_STORLEKSKLASSER, KP_DRIFTKRAV, KP_STATUSAR, KP_KALLOR,
  JA_NEJ_OKANT, KP_IT_MILJOER, KP_PROJEKTKONTOR, KP_FILOVERFORING, KP_ROLLER,
} from './kundprojekt';

export function Val({ label, value, onChange, lista, tom, ...rest }) {
  return (
    <TextField select size="small" fullWidth label={label} value={value || ''}
      onChange={e => onChange(e.target.value)} {...rest}>
      {tom && <MenuItem value=""><em>{tom}</em></MenuItem>}
      {lista.map(x => <MenuItem key={x.key} value={x.key}>{x.label}</MenuItem>)}
    </TextField>
  );
}

export function JaNejOkant({ label, value, onChange }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{label}</Typography>
      <ToggleButtonGroup size="small" exclusive value={value || 'okant'} onChange={(_, x) => x && onChange(x)}
        aria-label={label}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.75, py: 0.25,
          '&.Mui-selected': { bgcolor: '#1a1a1a', color: 'white', '&:hover': { bgcolor: '#333' } } } }}>
        {JA_NEJ_OKANT.map(x => <ToggleButton key={x.key} value={x.key}>{x.label}</ToggleButton>)}
      </ToggleButtonGroup>
    </Box>
  );
}

const Rubrik = ({ children }) => (
  <Divider textAlign="left" sx={{ mt: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</Typography>
  </Divider>
);

/** Namnet, med kodnamnsuppmaning och varning när kundprojektet är säkerhetsskyddat. */
export function NamnFalt({ kp, satt, autoFocus }) {
  const skyddat = kp.sakerhetsskydd === 'ja';
  return (
    <TextField size="small" fullWidth autoFocus={autoFocus} label="Namn på kundprojektet *" value={kp.namn}
      onChange={e => satt('namn', e.target.value)}
      helperText={skyddat ? `${SAKERHET_NAMN_HJALP} ${SAKERHET_VARNING}` : visaSakerhetsvarning(kp) ? SAKERHET_VARNING : undefined} />
  );
}

/** Grunddata utom namn och byggskede (som står först i formulären). */
export function GrunddataFalt({ kp, satt, medStatus = true }) {
  const varning = visaSakerhetsvarning(kp) ? SAKERHET_VARNING : undefined;
  return (
    <>
      <TextField size="small" fullWidth multiline minRows={2} label="Beskrivning" value={kp.beskrivning}
        onChange={e => satt('beskrivning', e.target.value)} helperText={varning} />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Autocomplete size="small" fullWidth options={SWEDISH_MUNICIPALITIES} value={kp.ort || null}
          onChange={(_, x) => satt('ort', x || '')}
          renderInput={params => <TextField {...params} label="Ort (kommun)" />} />
        <Val label="Typ" lista={KP_TYPER} tom="Välj typ" value={kp.typ} onChange={x => satt('typ', x)} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField size="small" fullWidth type="month" label="Skedestart" InputLabelProps={{ shrink: true }}
          value={kp.skedeStart} onChange={e => satt('skedeStart', e.target.value)}
          helperText="Månad då nuvarande eller nästa skede startar" />
        <Val label="Nästa skede" lista={KP_BYGGSKEDEN} tom="–" value={kp.nastaSkede} onChange={x => satt('nastaSkede', x)} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Val label="Entreprenadform" lista={KP_ENTREPRENADFORMER} value={kp.entreprenadform} onChange={x => satt('entreprenadform', x)} />
        <Val label="Storleksklass" lista={KP_STORLEKSKLASSER} value={kp.storleksklass} onChange={x => satt('storleksklass', x)} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Val label="Driftkrav" lista={KP_DRIFTKRAV} value={kp.driftkrav} onChange={x => satt('driftkrav', x)} />
        <TextField size="small" fullWidth type="number" label="Antal granskare (ungefär)"
          value={kp.antalGranskare ?? ''} onChange={e => satt('antalGranskare', e.target.value)} inputProps={{ min: 0 }} />
      </Box>
      <TextField size="small" fullWidth label="Verktyg i dag" value={kp.verktygIdag}
        onChange={e => satt('verktygIdag', e.target.value)} helperText={varning} />
      <Box sx={{ display: 'flex', gap: 2 }}>
        {medStatus && <Val label="Status" lista={KP_STATUSAR} value={kp.status} onChange={x => satt('status', x)} />}
        <Val label="Källa" lista={KP_KALLOR} tom="–" value={kp.kalla} onChange={x => satt('kalla', x)} />
      </Box>
    </>
  );
}

/**
 * Säkerhetssektionen. full=false: bara IT-miljö och projektkontor (snabb-
 * registreringen, och bara vid ja). full=true: alla fält, visas vid ja och
 * okänt, med varningstexten på fritextfältet.
 */
export function SakerhetFalt({ kp, satt, full }) {
  const synlig = full ? visaSakerhetsfalt(kp) : kp.sakerhetsskydd === 'ja';
  return (
    <>
      <JaNejOkant label="Säkerhetsskydd" value={kp.sakerhetsskydd} onChange={x => satt('sakerhetsskydd', x)} />
      {synlig && (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FFF8F2', border: '1px solid #FFCC80', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShieldOutlinedIcon sx={{ fontSize: 18, color: '#E65100' }} />
            <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 700 }}>
              Ange bara att något gäller – aldrig var, vad eller hur.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Val label="IT-miljö" lista={KP_IT_MILJOER} value={kp.itMiljo} onChange={x => satt('itMiljo', x)} />
            <Val label="Projektkontor" lista={KP_PROJEKTKONTOR} value={kp.projektkontor} onChange={x => satt('projektkontor', x)} />
          </Box>
          {full && (
            <>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <JaNejOkant label="Säkerhetsskyddsavtal" value={kp.sakerhetsskyddsavtal} onChange={x => satt('sakerhetsskyddsavtal', x)} />
                <JaNejOkant label="Externa parter behöver åtkomst" value={kp.externaParterBehoverAtkomst} onChange={x => satt('externaParterBehoverAtkomst', x)} />
              </Box>
              <Val label="Filöverföring" lista={KP_FILOVERFORING} value={kp.filoverforing} onChange={x => satt('filoverforing', x)} />
              <TextField size="small" fullWidth label="Granskning i dag" value={kp.granskningIdag}
                onChange={e => satt('granskningIdag', e.target.value)} helperText={SAKERHET_VARNING} inputProps={{ maxLength: 200 }} />
            </>
          )}
        </Box>
      )}
    </>
  );
}

// ── Parter ──────────────────────────────────────────────────────────────────

/** Kontakter per kund, lästa vid behov. [kontakterFor(kundId) → lista | null, ladda(kundId)] */
export function useKundKontakter() {
  const [map, setMap] = useState({});
  const startade = useRef(new Set());
  const ladda = useCallback(kundId => {
    if (!kundId || startade.current.has(kundId)) return;
    startade.current.add(kundId);
    getDocs(query(collection(db, 'crmCompanies', kundId, 'contacts'), orderBy('name')))
      .then(s => s.docs.map(d => ({ id: d.id, ...d.data() })))
      .catch(() => [])
      .then(lista => setMap(n => ({ ...n, [kundId]: lista })));
  }, []);
  return [kundId => map[kundId] ?? null, ladda];
}

function PartRad({ part, kund, kontakter, onAndra, onTaBort, kanTaBort }) {
  const valda = new Map((part.kontakter || []).map(k => [k.kontaktId, k.rollIProjektet || '']));
  const sattKontakter = m => onAndra({ ...part, kontakter: [...m].map(([kontaktId, rollIProjektet]) => ({ kontaktId, rollIProjektet })) });
  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }} noWrap>{kund?.name || 'Okänd kund'}</Typography>
        <Val label="Roll" lista={KP_ROLLER} tom="Ingen roll" value={part.roll}
          onChange={roll => onAndra({ ...part, roll })} sx={{ maxWidth: 220 }} />
        <Tooltip title={kanTaBort ? 'Ta bort part' : 'Ett kundprojekt behöver minst en part'}>
          <span>
            <IconButton size="small" onClick={onTaBort} disabled={!kanTaBort} aria-label={`Ta bort ${kund?.name || 'part'}`}
              sx={{ color: '#C62828' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {(part.leadIds || []).length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {part.leadIds.length} {part.leadIds.length === 1 ? 'lead kopplat' : 'leads kopplade'} – kopplas bort från leadet.
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        {kontakter == null ? <CircularProgress size={14} /> : !kontakter.length ? (
          <Typography variant="caption" color="text.secondary">Kunden har inga kontakter.</Typography>
        ) : kontakter.map(k => (
          <Box key={k.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox size="small" checked={valda.has(k.id)} inputProps={{ 'aria-label': `Koppla ${k.name}` }}
              onChange={e => { const m = new Map(valda); if (e.target.checked) m.set(k.id, ''); else m.delete(k.id); sattKontakter(m); }}
              sx={{ p: 0.5, '&.Mui-checked': { color: '#FF6B00' } }} />
            <Typography variant="body2" sx={{ minWidth: 150 }}>{k.name}</Typography>
            {valda.has(k.id) && (
              <TextField size="small" fullWidth placeholder="Roll i projektet" value={valda.get(k.id)}
                onChange={e => sattKontakter(new Map(valda).set(k.id, e.target.value))} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Lägg till och ta bort parter, ändra roll, koppla och avkoppla kontakter
 * med roll i projektet. companies: alla kunder (för "Lägg till part").
 */
export function ParterFalt({ kp, setKp, companies, kontakterFor, laddaKontakter }) {
  const kundMap = new Map(companies.map(c => [c.id, c]));
  const upptagna = new Set(kp.parter.map(p => p.kundId));
  React.useEffect(() => { kp.parter.forEach(p => laddaKontakter(p.kundId)); }, [kp.parter, laddaKontakter]);

  return (
    <>
      <Rubrik>Parter</Rubrik>
      {kp.parter.map((p, i) => (
        <PartRad key={`${p.kundId}-${i}`} part={p} kund={kundMap.get(p.kundId)} kontakter={kontakterFor(p.kundId)}
          kanTaBort={kp.parter.length > 1}
          onAndra={ny => setKp(k => ({ ...k, parter: k.parter.map((x, j) => (j === i ? ny : x)) }))}
          onTaBort={() => setKp(k => ({ ...k, parter: k.parter.filter((_, j) => j !== i) }))} />
      ))}
      <Autocomplete size="small" options={companies} value={null} blurOnSelect clearOnBlur
        getOptionLabel={c => c?.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
        getOptionDisabled={c => upptagna.has(c.id)}
        onChange={(_, c) => { if (c) setKp(k => ({ ...k, parter: [...k.parter, nyPart(c.id)] })); }}
        renderInput={params => <TextField {...params} label="Lägg till part (kund)" />} />
    </>
  );
}

/** Testkörning för kopplingen mellan ett lead och kundprojektet. */
export function TestkorningFalt({ varde, onChange, kontakter }) {
  const t = varde || { start: '', slut: '', ansvarigKontaktId: '' };
  const satt = (falt, x) => onChange({ ...t, [falt]: x });
  return (
    <>
      <Rubrik>Testkörning för det här leadet</Rubrik>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField size="small" fullWidth type="date" label="Start" InputLabelProps={{ shrink: true }}
          value={t.start} onChange={e => satt('start', e.target.value)} />
        <TextField size="small" fullWidth type="date" label="Slut" InputLabelProps={{ shrink: true }}
          value={t.slut} onChange={e => satt('slut', e.target.value)}
          error={!!(t.start && t.slut && t.slut < t.start)}
          helperText={t.start && t.slut && t.slut < t.start ? 'Slut före start' : undefined} />
        <TextField select size="small" fullWidth label="Ansvarig kontakt" value={t.ansvarigKontaktId}
          onChange={e => satt('ansvarigKontaktId', e.target.value)}>
          <MenuItem value=""><em>Ingen</em></MenuItem>
          {kontakter.map(k => <MenuItem key={k.id} value={k.id}>{k.name}{k.kund ? ` (${k.kund})` : ''}</MenuItem>)}
        </TextField>
      </Box>
    </>
  );
}

export { Rubrik as FaltRubrik };
